<template>
<template v-if="useIsMobile().value">
  <KHeaderBar title-text="New Arrival" :bg-color="scrollTop > 120 ? '#fff' : ''" >
    <template #right>
      <KSShareStoreShareButton
        shareImageUrl="https://img.kilimall.com/c/h5/imgs/share-new-arrivals.png"
        shareTitle="🎉 Guess what? Kilimall just dropped some exciting new products! 🛍️💥 You won't want to miss these! Let’s dive in and see what caught my eye—tap the link now! 💖"
        sourceType="NewArrival"
      />
    </template>
  </KHeaderBar>
  <div class="banners" v-show="banners.length > 0">
    <van-swipe class="my-swipe" :autoplay="3000" indicator-color="#DD3131">
      <van-swipe-item
        v-for="(banner, idx) in banners"
        :key="idx"
      >
        <img :src="banner.imageUrl" alt="" lazy="loading" @click="handleBannerClick(banner)">
      </van-swipe-item>
    </van-swipe>
  </div>
  <div class="banner-none" v-if="banners.length <= 0" />

  <div class="product-wrapper">
    <div class="all-ctg-wrapper">
      <div class="more-ctg-btn" @click="handleShowAllCtgClick">
        <van-icon name="arrow-down" />
      </div>
      <div v-show="showAllCtg" class="all-ctg-card">
        <div class="header-box">
          <span>All Categories</span>
          <div @click="handleShowAllCtgClick">
            <van-icon name="arrow-up"  />
          </div>
        </div>
        <div class="inner-ctg-card">
          <div
            v-for="(ctg, idx) in newCategories"
            :key="ctg.key"
            class="ctg-tag"
            :select="ctg.key === currentCategoryKey ? 1 : 0"
            @click="handleCtgClick(idx)"
          >{{ ctg.name }}</div>
        </div>
      </div>
    </div>

    <van-tabs
      v-model:active="ctgActive"
      sticky
      swipeable
      :ellipsis="false"
      @change="handleSwitchTabChange"
    >
      <van-tab
        v-for="ctg in newCategories"
        :key="ctg.key"
        :title="ctg.name"
      > 
        <van-pull-refresh
          :model-value="newArrivalListMap[ctg.key]?.loadingStatus === 'refresh'"
          @refresh="onRefresh"
        >
          <van-list
            :loading="newArrivalListMap[ctg.key]?.loadingStatus === 'loading'"
            :finished="newArrivalListMap[ctg.key]?.loadingStatus === 'over'"
            finished-text="Sorry, there is no data"
            loading-text="loading..."
            :immediate-check="false"
            :offset="800"
            @load="loadMore"
          >
            <div class="product-list-wrapper">
              <van-skeleton :row="16" :loading="newArrivalListMap[ctg.key]?.loadingStatus === 'refresh'">
                <div class="product-list">
                  <div 
                    v-for="item in newArrivalListMap[ctg.key]?.list || []"
                    :key="item.listingId"
                    class="product-item"
                  >
                    <div class="inner">
                      <KProductItem :productData="item" :source="item.source" />
                    </div>
                  </div>
                </div>

                <template #template>
                  <div style="display: flex;flex-wrap: wrap;justify-content: center;width: 100%;">
                    <div
                      v-for="i in 6"
                      :key="i"
                      style="width: 50%;padding: 10px;display: flex;flex-direction: column;align-items: center;"
                    >
                      <div style="height: 0;width:100%;padding-bottom: 100%;position: relative;">
                        <van-skeleton-image style="position: absolute;top: 0;left: 0;width: 100%;height: 100%;" />
                      </div>
                      <van-skeleton-paragraph />
                      <van-skeleton-paragraph />
                    </div>
                  </div>
                </template>
              </van-skeleton>
            </div>
          </van-list>
        </van-pull-refresh>
      </van-tab>
    </van-tabs>
  </div>
</template>

<PCNewArrival
  v-else
  :listings="newArrivalListMap[currentCategoryKey]?.list || []"
  :loadingStatus="newArrivalListMap[currentCategoryKey]?.loadingStatus || 'loaded'"
  :newCategories="newCategories"
  :selectKey="currentCategoryKey"
  @load="loadMore"
  @category:change="handleSwitchTabChange"
/>
</template>

<script setup lang="ts">
import { type INewArrivalParams, newArrival, setUrlCache } from '@/api/models/system'
import PCNewArrival from '@/pages-pc/NewArrival.vue'
import { buildWhatsNewFullBannerSource, buildBannerIdNameSourceValue, buildWhatsNewPageListingSource, buildWhatsNewRecommendSource, getHotKeysLinkTypeLatestPage, getListingDetailHref, resolveWhatsNewPageFirstPage, WHATS_NEW_FIRST_TRAFFIC_SOURCE_PAGE } from '@/plugins/utils'

export interface IListing {
  listingId: number;
  price: number;
  discountPrice: number;
  minPrice: number;
  maxPrice: number;
  minListPrice: number;
  maxListPrice: number;
  image: string;
  title: string;
  storeName: string;
}
export interface INewArrivalCategory {
  key: string,
  categoryId: number;
  name: string;
  bindCategoryIds: number[];
  bindKeywords: any[];
}
interface INewArrivalListMap {
  loadingStatus: 'over' | 'loading' | 'refresh' | 'loaded',
  payload: INewArrivalParams,
  list: IListing[]
}
definePageMeta({ layout: 'wap' })
const headers = useRequestHeaders()
const proto = headers['x-forwarded-proto'] || 'https'
const host = `${proto}://${process.server ? headers.host : window.location.host}`

useHead({
  link: [
    {
      rel: "canonical",
      href: `${host}/new-arrival`
    }
  ],
  meta: [
    { content: `${host}/new-arrival?source=Share|newArrival|`, property: 'og:url' },
    { content: 'New Arrival', property: 'og:title' },
    { content: 'https://img.kilimall.com/c/h5/imgs/share_whatsnew_bg@2x.png', property: 'og:image' },
    { content: 'website', property: 'og:type' },
    { content: 'Shop the freshest arrivals on Kilimall! Our new products feature the best deals and latest trends in Kenya. Find exactly what you need and elevate your shopping experience today!', property: 'og:description' }
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "name": "New Arrival Breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": host
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "New Arrival",
            "item": `${host}/new-arrival`
          }
        ]
      })
    }
  ]
})

const router = useRouter()
const route = useRoute()
const whatsNewListingFirstPage = resolveWhatsNewPageFirstPage(route)
const { $filterBanners, $throttle, $hotKeysJump } = useNuxtApp()
const banners = computed(() => $filterBanners(useGlobalJsonData().value.banner_app_v2?.data?.newArrival || []))

/** 新品频道 Banner 跳转 source */
const handleBannerClick = async (banner: IBanner) => {
  const item = { linkType: banner.configType, linkValue: banner.value }
  const sourceValue = buildBannerIdNameSourceValue(banner)
  const isProductJump = [5, 10].includes(banner.configType)
  const source = buildWhatsNewFullBannerSource(
    sourceValue,
    whatsNewListingFirstPage,
    isProductJump
      ? WHATS_NEW_FIRST_TRAFFIC_SOURCE_PAGE
      : getHotKeysLinkTypeLatestPage(banner.configType)
  )

  if (isProductJump) {
    const listingParams = banner.configType === 10
      ? { type: 'sku' as const, source }
      : { source }
    const { url, key, cookie } = getListingDetailHref(banner.value, listingParams)
    await setUrlCache(cookie, key)
    router.push(url)
    return
  }

  $hotKeysJump(item, true, source)
}

const ctgActive = ref<number>(0)
const currentCategoryKey = computed(() => newCategories.value[ctgActive.value]?.key)
const newCategories = ref<INewArrivalCategory[]>([{ categoryId: 0, bindCategoryIds: [], bindKeywords: [], name: 'Recommend', key: '00_0' }])
const newArrivalListMap = ref<{ [key: string]: INewArrivalListMap }>({ '00_0': { list: [], loadingStatus: 'loaded', payload: { lastId: -1, limit: 20 } } })

const isRecommendCategory = (categoryKey?: string) => {
  const category = newCategories.value.find(c => c.key === categoryKey)
  return category?.categoryId === 0
}

const attachWhatsNewRecommendSource = (listings: any[] = [], categoryKey?: string) => {
  const category = newCategories.value.find(c => c.key === categoryKey)

  listings.forEach((c: any) => {
    if (isRecommendCategory(categoryKey)) {
      c.source = buildWhatsNewRecommendSource(c.modelName || c.algorithmModelName || '')
    } else {
      c.source = buildWhatsNewPageListingSource(category?.name || '', whatsNewListingFirstPage, route)
    }
  })
  return listings
}

const initNewArrival = async(type='load') => {
  const currentNewArrivalCategory = newArrivalListMap.value[currentCategoryKey.value]
  
  if(currentNewArrivalCategory?.loadingStatus === 'over') {
    return
  }

  currentNewArrivalCategory.loadingStatus = type === 'load' ? 'loading' : 'refresh'

  try {
    const { listings, categories } = await newArrival(currentNewArrivalCategory.payload, false)
    if(newCategories.value.length <= 1) {
      newCategories.value
        .splice(1, newCategories.value.length, ...(categories || []).map((c: any, idx: number) => ({ ...c, key: `${c.categoryId}_${idx}`})))
        

      newCategories.value.slice(1).forEach((ctg: INewArrivalCategory, idx: number) => {
        const { bindCategoryIds, bindKeywords, key } = ctg
        const _payload: INewArrivalParams = { lastId: -1, limit: 20 }
        if(bindCategoryIds.length > 0) {
          _payload.categoryIds = bindCategoryIds
        }
        if(bindKeywords.length > 0) {
          _payload.keywords = bindKeywords.join(' ')
        }

        newArrivalListMap.value[key] = {
          loadingStatus: 'loaded',
          payload: _payload, // : { lastId: -1, limit: 20, categoryId, categoryIds: bindCategoryIds, keywords: bindKeywords.join(' ') },
          list: []
        }
      })
    }

    if (type === 'refresh') {
      currentNewArrivalCategory.list = attachWhatsNewRecommendSource(listings || [], currentCategoryKey.value)
      currentNewArrivalCategory.loadingStatus = 'loading'
    } else {
      currentNewArrivalCategory.list.push(...attachWhatsNewRecommendSource(listings || [], currentCategoryKey.value))
    }

    currentNewArrivalCategory.payload.lastId = listings[listings.length - 1]?.listingId || -1

    currentNewArrivalCategory.loadingStatus = currentNewArrivalCategory.payload.limit > listings.length ? 'over' : 'loaded'
  } catch(e) {
    console.error(e)
    currentNewArrivalCategory.loadingStatus = 'loaded'
  }
}

const scrollTop = ref<number>(0)
const scroll = $throttle(() => {
  scrollTop.value = document.documentElement.scrollTop

  showAllCtg.value = false
}, 15)
onMounted(async() => {
  await initNewArrival('refresh')
  useIsMobile().value && window.addEventListener('scroll', scroll)
})
onUnmounted(() => {
  useIsMobile().value && window.removeEventListener('scroll', scroll)
})

const handleSwitchTabChange = async(e: any) => {
  ctgActive.value = e
  const curCtgList = newArrivalListMap.value[String(currentCategoryKey.value)]
  if(['loading', 'over', 'refresh'].includes(curCtgList?.loadingStatus)) {
    return
  }

  if(!curCtgList || curCtgList?.list.length <= 0) {
    await initNewArrival('refresh')
  }
}

const handleCtgClick = (e: number) => {
  showAllCtg.value = false

  handleSwitchTabChange(e)
}
const loadMore = async() => {
  const { loadingStatus } = newArrivalListMap.value[currentCategoryKey.value]

  if(['loading', 'over', 'refresh'].includes(loadingStatus)) {
    return
  }

  await initNewArrival('load')
}

const onRefresh = async() => {
  newArrivalListMap.value[currentCategoryKey.value].loadingStatus = 'refresh'
  newArrivalListMap.value[currentCategoryKey.value].payload.lastId = -1

  await initNewArrival('refresh')
}

const showAllCtg = ref<boolean>(false)
const handleShowAllCtgClick = () => {
  showAllCtg.value = !showAllCtg.value
}
</script>

<style lang="scss" scoped>
.all-ctg-wrapper{
  position: sticky;
  top: 86px;
  right: 0;
  left: 0;
  z-index: 9999;
}
.more-ctg-btn{
  background-color: #fff;
  height: var(--van-tabs-line-height);
  width: 80px;
  @include center();
  position: absolute;
  right: 0;
  top: 0;
  z-index: 10002;
  i { font-size: 32px; }
}

.all-ctg-card{
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0; // var(--van-tabs-line-height);
  z-index: 10003;
  height: 100vh;
  background-color: rgba($color: #000000, $alpha: 0.4);
  .header-box{
    width: 100%;
    padding: 30px 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #fff;
    i{font-size: 32px}
  }
  .inner-ctg-card{
    background-color: #fff;
    display: flex;
    flex-wrap: wrap;
    max-height: 800px;
    overflow-y: auto;
    .ctg-tag{
      line-height: 80px;
      margin: 0 40px;
      font-size: 26px
    }
    .ctg-tag[select="1"]{
      font-weight: 600;
    }
  }
}
.product-list-wrapper{
  background-color: #fff;
}
.product-list{
  background-color: #f5f5f5;
  display: flex;
  flex-wrap: wrap;
  padding: 10px;
  .product-item{
    width: 50%;
    overflow: hidden;
    padding: 10px;
    box-sizing: border-box;
    margin-bottom: 15px;
    .inner{
      border-radius: 8px;
      overflow: hidden;
      background-color: #fff;
    }
  }
}
.product-wrapper{
  position: relative;
}
.product-wrapper::v-deep(.van-sticky--fixed) {
  top: 86px !important;
}
.product-wrapper::v-deep(.van-tabs__wrap) {
  padding-right: 80px;
  position: relative;
}
.banners{
  width: 100%;
  padding-bottom: 59.91%;
  height: 0;
  position: relative;
  .my-swipe{
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: cover;
    img{
      width: 100%;
    }
  }
}
.banner-none {
  width: 100%;
  height: 86px;
}
.k-header-bar :deep() .share-button-div i {
  color: #333333;
}
</style>